// useLoginAuthActions.js

import { auth } from 'firebaseConfig';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';

import { sendAccountVerificationEmail } from 'services/auth/EmailVerificationService';
import { signInWithGoogleAndEnsureUserNode } from 'services/auth/GoogleAuthService';
import { sendPasswordReset } from 'services/auth/PasswordResetService';
import { setNodeDoc } from 'services/nodes/NodeWriteService';
import { getUser } from 'services/users/UserService';

import { SectionType } from 'utilities/Enums';
import { getDefaultNode } from 'utilities/NodeFactory';
import { getSectionPath } from 'utilities/Routing';
import { generateSubstrings } from 'utilities/Strings';
import { buildUserSummary, getDefaultUser } from 'utilities/Users';

import { SIGNUPS_ENABLED, SIGNUPS_PAUSED_MESSAGE } from 'login/signupAvailability';

const buildSignupDebugContext = (stage, details = {}) => ({
  stage,
  at: new Date().toISOString(),
  ...details,
});

const mapPasswordResetRequestError = (error) => {
  const code = error?.code || '';

  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  return 'Unable to send a reset email right now. Please try again.';
};

/**
 * Prefer the live Workspace rail URL over a stale stored redirect.
 *
 * @param {string|null|undefined} postAuthRedirect
 * @returns {string|null}
 */
function resolvePostAuthTarget(postAuthRedirect) {
  if (typeof window !== 'undefined'
    && window.location.pathname.startsWith('/workspace/sidebar')) {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }
  const target = String(postAuthRedirect || '').trim();
  return target || null;
}

const useLoginAuthActions = ({
  loginFormValid,
  loginEmail,
  loginPassword,
  forgotFormValid,
  forgotEmail,
  markForgotSent,
  signupFormValid,
  signupEmail,
  signupName,
  signupPassword,
  postAuthRedirect,
  setPostAuthRedirect,
  setCurrentSection,
  showProgress,
  hideProgress,
  navigate,
  setErrorMessage,
  setUser,
  setUserSettings,
  setActiveBase,
  setNewUserProfileSetup,
  setOpen,
}) => {

  /**
   * handleLogin
   *
   * Handles login.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleLogin = async () => {
    if (!loginFormValid) return;

    try {
      showProgress('Logging in...');

      // Sign in first so USER profile reads run under auth. Pre-auth
      // email lookups against _nodes are blocked by Firestore rules.
      await signInWithEmailAndPassword(
        auth,
        loginEmail.toLowerCase(),
        loginPassword
      );

      const authUser = auth.currentUser;
      const fetchedUser = await getUser(authUser.uid);
      setUser({
        ...(fetchedUser || {}),
        emailVerified: authUser.emailVerified,
      });

      if (!authUser.emailVerified) {
        setOpen(false);
        return;
      }

      const targetPath = resolvePostAuthTarget(postAuthRedirect);
      if (targetPath) {
        setPostAuthRedirect(null);
        navigate(targetPath, { replace: true });
      } else {
        setCurrentSection(SectionType.HOME);
        navigate(getSectionPath(SectionType.HOME));
      }
    } catch (error) {
      setErrorMessage('Login failed. Please check your credentials.');
    } finally {
      hideProgress();
    }
  };

  /**
   * handleSignup
   *
   * Handles signup.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSignup = async () => {
    if (!SIGNUPS_ENABLED) {
      setErrorMessage(SIGNUPS_PAUSED_MESSAGE);
      return;
    }
    if (!signupFormValid) return;

    showProgress('Creating account...');

    const signupAttemptId = `signup:${Date.now()}`;
    console.info('[signup] start', buildSignupDebugContext('start', {
      signupAttemptId,
      email: signupEmail,
    }));

    try {
      console.info('[signup] auth:createUser:start', buildSignupDebugContext('auth:createUser:start', {
        signupAttemptId,
        email: signupEmail,
      }));
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupEmail,
        signupPassword
      );
      const authUser = userCredential.user;
      const uid = authUser.uid;
      console.info('[signup] auth:createUser:success', buildSignupDebugContext('auth:createUser:success', {
        signupAttemptId,
        uid,
        currentUserUid: auth.currentUser?.uid || null,
      }));

      const profile = getDefaultUser(
        uid,
        signupName,
        signupEmail.toLowerCase()
      );

      const defaultNode = getDefaultNode('USER');
      const index = generateSubstrings(signupName.toLowerCase(), 5);

      const profileNode = {
        ...defaultNode,
        type: 'USER',
        key: uid,
        userKey: uid,
        title: signupName,
        emailVerified: false,
        user: buildUserSummary({ data_profile: profile }),
        data_profile: profile,
        path: [],
        permissions: {
          VIEW: { type: 'EVERYONE' },
          CONTRIBUTE: { type: 'ME' },
          EDIT: { type: 'ME' },
          DELETE: { type: 'ME' },
          COMMENT: { type: 'EVERYONE' },
          SHARE: { type: 'EVERYONE' },
        },
        index,
      };

      const safePayload = JSON.parse(JSON.stringify(profileNode));
      console.info('[signup] userNode:create:start', buildSignupDebugContext('userNode:create:start', {
        signupAttemptId,
        uid,
      }));
      await setNodeDoc(
        uid,
        {
          ...safePayload,
        },
        { merge: false }
      );
      console.info('[signup] userNode:create:success', buildSignupDebugContext('userNode:create:success', {
        signupAttemptId,
        uid,
      }));

      console.info('[signup] verificationEmail:send:start', buildSignupDebugContext('verificationEmail:send:start', {
        signupAttemptId,
        uid,
      }));
      await sendAccountVerificationEmail(authUser);
      console.info('[signup] verificationEmail:send:success', buildSignupDebugContext('verificationEmail:send:success', {
        signupAttemptId,
        uid,
      }));

      setUser({
        key: uid,
        email: authUser.email,
        displayName: authUser.displayName,
        emailVerified: false,
        phoneNumber: authUser.phoneNumber,
        userKey: uid,
        ...safePayload,
      });

      console.info('[signup] complete', buildSignupDebugContext('complete', {
        signupAttemptId,
        uid,
        awaitingEmailVerification: true,
      }));

      setOpen(false);
    } catch (error) {
      console.error('[signup] failed', buildSignupDebugContext('failed', {
        signupAttemptId,
        code: error?.code || null,
        message: error?.message || String(error),
        currentUserUid: auth.currentUser?.uid || null,
      }), error);
      setErrorMessage(error.message || 'Signup failed. Please try again.');
    } finally {
      hideProgress();
    }
  };

  /**
   * handleSendPasswordReset
   *
   * Handles send password reset.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSendPasswordReset = async () => {
    if (!forgotFormValid) return;

    try {
      showProgress('Sending reset link...');
      setErrorMessage(null);

      await sendPasswordReset(forgotEmail);
      markForgotSent();
    } catch (error) {
      console.error('[auth] password reset request failed:', error);
      setErrorMessage(mapPasswordResetRequestError(error));
    } finally {
      hideProgress();
    }
  };

  /**
   * handleGoogleSignup
   *
   * Handles google signup.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleGoogleSignup = async () => {
    setErrorMessage(null);

    try {
      showProgress('Signing in with Google...');

      const { user, authUser, isNewUser } = await signInWithGoogleAndEnsureUserNode();
      setUser({
        ...user,
        emailVerified: authUser.emailVerified,
      });

      if (!authUser.emailVerified) {
        setOpen(false);
        return;
      }

      if (isNewUser) {
        setNewUserProfileSetup(true);
      }

      const targetPath = resolvePostAuthTarget(postAuthRedirect);
      if (targetPath) {
        setPostAuthRedirect(null);
        navigate(targetPath, { replace: true });
      } else {
        setCurrentSection(SectionType.HOME);
        navigate(getSectionPath(SectionType.HOME));
      }

      setOpen(false);
    } catch (error) {
      console.error('[auth] Google signup failed:', error);
      setErrorMessage(error?.message || 'Google signup failed. Please try again.');
    } finally {
      hideProgress();
    }
  };

  return {
    handleLogin,
    handleSignup,
    handleSendPasswordReset,
    handleGoogleSignup,
  };
};

export default useLoginAuthActions;
